import type {SelectField} from '@prismicio/client'
import cn from 'classnames'
import type {
  CtaV2SliceSmallPrimaryRatingsItem,
  HeadlineSectionSliceDefaultItem,
  HeroV2SliceBentoGridPrimaryRatingsItem,
  HeroV2SliceDefaultItem,
  HeroV3SliceDefaultPrimaryRatingsItem,
} from 'prismicio-types'
import {useState} from 'react'
import {useEffectOnce} from 'react-use'
import type {IconTagContainerColor} from 'src/common/IconTag'
import {AnimateRatings} from './AnimatedRatings'
import {IconRatings} from './IconRatings'
import {StarRatings} from './StarRatings'

type RatingsSectionProps = {
  ratings:
    | HeroV2SliceDefaultItem[]
    | HeadlineSectionSliceDefaultItem[]
    | HeroV2SliceBentoGridPrimaryRatingsItem[]
    | HeroV3SliceDefaultPrimaryRatingsItem[]
    | CtaV2SliceSmallPrimaryRatingsItem[]
  type: SelectField<'Icons' | 'Stars' | 'Animated', 'filled'>
  centered?: boolean
  centeredMobile?: boolean
  version?: 'v1' | 'v2'
  iconContainerColor?: IconTagContainerColor
  darkBackground?: boolean
}

export const RatingsSection = ({
  ratings,
  type,
  centered = true,
  centeredMobile = true,
  version = 'v1',
  iconContainerColor,
  darkBackground = false,
}: RatingsSectionProps) => {
  const [activeIndex, setActiveIndex] = useState(0)

  // Set interval to animate ratings
  useEffectOnce(() => {
    if (type === 'Animated') {
      const interval = setInterval(() => {
        setActiveIndex((prev) => (prev === 0 ? 1 : 0))
      }, 2500)

      return () => clearInterval(interval)
    }

    return
  })

  if (ratings.length === 0) {
    return null
  }

  let RatingsComponent

  switch (type) {
    case 'Stars':
      RatingsComponent = StarRatings
      break
    case 'Animated':
      RatingsComponent = AnimateRatings
      break
    default:
      RatingsComponent = IconRatings
  }

  return (
    <div
      className={cn('flex justify-center', {
        'gap-3': type !== 'Animated',
        [`relative ${version === 'v1' ? 'h-7' : 'h-[38px]'} w-full items-center`]: type === 'Animated',
        'md:justify-start': !centered,
        'max-md:justify-start': !centeredMobile,
      })}
    >
      {ratings.map((review, index) => (
        <RatingsComponent
          key={index}
          review={review}
          isActive={activeIndex === index}
          version={type === 'Animated' ? version : undefined}
          iconContainerColor={type === 'Animated' ? iconContainerColor : undefined}
          darkBackground={darkBackground}
        />
      ))}
    </div>
  )
}
